import { supabase } from '../lib/supabase'
import type { 
  AdminSettingKey, 
  AdminSettingCategory,
  GetSettingsResponse,
  UpdateSettingResponse,
  GetSettingResponse
} from '../types/adminSettings'

export class AdminSettingsService {
  // Get all settings (admin only)
  static async getAllSettings(): Promise<GetSettingsResponse> {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .order('category', { ascending: true })
        .order('key', { ascending: true })

      if (error) {
        console.error('Error fetching admin settings:', error)
        return { success: false, data: [], error: error.message }
      }

      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching admin settings:', error)
      return { success: false, data: [], error: 'Failed to fetch settings' }
    }
  }

  // Get settings by category (admin only)
  static async getSettingsByCategory(category: AdminSettingCategory): Promise<GetSettingsResponse> {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .eq('category', category)
        .order('key', { ascending: true })

      if (error) {
        console.error('Error fetching settings by category:', error)
        return { success: false, data: [], error: error.message }
      }

      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching settings by category:', error)
      return { success: false, data: [], error: 'Failed to fetch settings' }
    }
  }

  // Get a specific setting by key
  static async getSettingByKey(key: AdminSettingKey): Promise<GetSettingResponse> {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .eq('key', key)
        .single()

      if (error) {
        console.error('Error fetching setting by key:', error)
        return { success: false, error: error.message }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Error fetching setting by key:', error)
      return { success: false, error: 'Failed to fetch setting' }
    }
  }

  // Get public settings (accessible to all users)
  static async getPublicSettings(): Promise<GetSettingsResponse> {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .eq('is_public', true)
        .order('category', { ascending: true })
        .order('key', { ascending: true })

      if (error) {
        console.error('Error fetching public settings:', error)
        return { success: false, data: [], error: error.message }
      }

      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching public settings:', error)
      return { success: false, data: [], error: 'Failed to fetch public settings' }
    }
  }

  // Update a setting (admin only)
  static async updateSetting(key: AdminSettingKey, value: string | number | boolean | object | null): Promise<UpdateSettingResponse> {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('key', key)
        .select()
        .single()

      if (error) {
        console.error('Error updating setting:', error)
        return { success: false, error: error.message }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Error updating setting:', error)
      return { success: false, error: 'Failed to update setting' }
    }
  }

  // Create a new setting (admin only)
  static async createSetting(
    key: AdminSettingKey,
    value: string | number | boolean | object | null,
    description?: string,
    category: AdminSettingCategory = 'general',
    isPublic: boolean = false
  ): Promise<UpdateSettingResponse> {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .insert({
          key,
          value,
          description,
          category,
          is_public: isPublic
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating setting:', error)
        return { success: false, error: error.message }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Error creating setting:', error)
      return { success: false, error: 'Failed to create setting' }
    }
  }

  // Delete a setting (admin only, use with caution)
  static async deleteSetting(key: AdminSettingKey): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('admin_settings')
        .delete()
        .eq('key', key)

      if (error) {
        console.error('Error deleting setting:', error)
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      console.error('Error deleting setting:', error)
      return { success: false, error: 'Failed to delete setting' }
    }
  }

  // Helper method to get a setting value with fallback
  static async getSettingValue<T>(key: AdminSettingKey, fallback: T): Promise<T> {
    try {
      const response = await this.getSettingByKey(key)
      if (response.success && response.data) {
        return response.data.value as T
      }
      return fallback
    } catch (error) {
      console.error('Error getting setting value:', error)
      return fallback
    }
  }

  // Batch update multiple settings
  static async updateMultipleSettings(
    updates: Array<{ key: AdminSettingKey; value: string | number | boolean | object | null }>
  ): Promise<{ success: boolean; errors: string[] }> {
    const errors: string[] = []
    
    for (const update of updates) {
      const result = await this.updateSetting(update.key, update.value)
      if (!result.success) {
        errors.push(`Failed to update ${update.key}: ${result.error}`)
      }
    }

    return {
      success: errors.length === 0,
      errors
    }
  }
}